import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ClientDetailsForm from '../new-order-components/ClientDetailsForm';
import OrderStatusSelector from '../new-order-components/OrderStatusSelector';
import OrderItemsTable from '../new-order-components/OrderItemsTable';
import OrderSummary from '../new-order-components/OrderSummary';
import { useOrderForm } from '../new-order-components/useOrderForm';
import { fetchOrderById } from '../orders-controller/order-controller';
import PlatformSelector from '../new-order-components/PlatformSelector';
import DepotSelector from '../components/DepotSelector';

const EditOrder = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const dataLoadedRef = useRef(false);
    
    const {
        formData,
        setFormData,
        orderItems,
        setOrderItems,
        errors,
        totals,
        updateTotals,
        statusOptions,
        handleInputChange,
        handleStatusChange,
        handleAddItem,
        handleUpdateItem,
        handleRemoveItem,
        handleSubmit,
        isSubmitting,
        promoCodeState,
        handlePromoCodeBlur,
        setExistingPromoCodeData,
        isEditMode
    } = useOrderForm(navigate, true);

    // Fetch order data when component mounts
    useEffect(() => {
        if (dataLoadedRef.current) return;
        
        const fetchOrderData = async () => {
            try {
                setLoading(true);
                const orderData = await fetchOrderById(id);
                
                if (orderData.entet && orderData.line) {
                    
                    // Set form data from fetched order
                    const formDataToSet = {
                        reference: orderData.entet.reference || '',
                        dateBonCommande: orderData.entet.dateBonCommande || new Date().toISOString(),
                        etat: orderData.entet.etat || 'pending',
                        platformId: orderData.entet.plateformId?.toString() || '',
                        depotId: orderData.entet.depotId?.toString() || '',
                        client: {
                            firstName: orderData.entet.clientFirstName || '',
                            lastName: orderData.entet.clientLastName || '',
                            address: orderData.entet.clientAdresse || '',
                            city: orderData.entet.ville || '',
                            phone: orderData.entet.clientNumber || '',
                            promoCode: orderData.entet.codePromo || '',
                            email: '',
                            postalCode: ''
                        },
                        notes: orderData.entet.notes || '',
                        paymentMethod: 'especes'
                    };
                    
                    setFormData(formDataToSet);
                    
                    // Set order items from fetched order lines
                    const mappedItems = orderData.line.map(item => {
                        const productName = item.product?.name || 'Unknown Product';
                        const variantName = item.productVariant?.name || '';
                        
                        const fullName = item.productVariantId ? 
                            `${productName} - ${variantName}` : 
                            productName;
                        
                        const reference = item.productVariant?.reference || item.product?.reference || `Ref-${item.productId}`;
                        
                        let tvaRate = item.tauxTva || 19;
                        if (!tvaRate && item.totalHt && item.totalTva) {
                            tvaRate = Math.round((item.totalTva / item.totalHt) * 100);
                        }

                        const qty = Number(item.quantite) || 1;

                        // Fallback for prixUnitaire: recover from totalTTC + totalRemise when stored as 0
                        let unitPrice = Number(item.prixUnitaire) || 0;
                        if (!unitPrice) {
                            const ttc = Number(item.totalTTC) || 0;
                            const remise = Number(item.totalRemise) || 0;
                            if (ttc > 0 && qty > 0) {
                                unitPrice = (ttc + remise) / qty;
                            } else if (item.productVariant?.prixVenteTTC || item.product?.prixVenteTTC) {
                                unitPrice = Number(item.productVariant?.prixVenteTTC || item.product?.prixVenteTTC) || 0;
                            } else if (item.productVariant?.prixVenteHt || item.product?.prixVenteHt) {
                                unitPrice = Number(item.productVariant?.prixVenteHt || item.product?.prixVenteHt) * (1 + (tvaRate || 19) / 100);
                            }
                        }

                        let remiseRate = Number(item.tauxRemise) || 0;
                        if (!remiseRate && Number(item.totalRemise) > 0 && unitPrice > 0 && qty > 0) {
                            const basePrice = unitPrice * qty;
                            remiseRate = Math.round((Number(item.totalRemise) / basePrice) * 100);
                        }

                        return {
                            id: item.id?.toString() || Math.random().toString(36).substr(2, 9),
                            dbId: item.id,
                            productId: item.productId || null,
                            variantId: item.productVariantId || null,
                            name: fullName,
                            reference: reference,
                            price: Number(unitPrice.toFixed(2)),
                            quantity: qty,
                            tva: tvaRate,
                            remise: remiseRate,
                            stock: item.productVariant?.stock || item.product?.stock || 100,
                            isVariant: !!item.productVariantId
                        };
                    });
                    
                    setOrderItems(mappedItems);
                    
                    // Set promo code data if exists - LET useOrderForm HANDLE THE CALCULATION
                    if (orderData.entet.codePromo) {
                        
                        await setExistingPromoCodeData(
                            orderData.entet.codePromo, 
                            orderData.entet.promoDiscount
                        );
                        
                    }
                    
                    dataLoadedRef.current = true;
                } else {
                    throw new Error("Invalid order data format");
                }
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching order:", error);
                setError(error.message || "Failed to load order data");
                setLoading(false);
            }
        };
        
        fetchOrderData();
    }, [id]); // REMOVE ALL OTHER DEPENDENCIES

    const onSubmitForm = (e) => {
        handleSubmit(e, id);
    };

    if (loading) {
        return (
            <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto bg-white shadow rounded-lg p-8">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Order</h1>
                    <p className="text-gray-700 mb-4">{error}</p>
                    <Link 
                        to="/admin/orders"
                        state={{ fromOrderEdit: true }}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none"
                    >
                        Return to Orders
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto mb-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Link to="/admin/orders" state={{ fromOrderEdit: true }} className="text-[#174540] hover:text-[#0d2926]">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">Modifier la Commande #{id}</h1>
                    </div>
                    
                    <Link
                        to="/admin/orders"
                        state={{ fromOrderEdit: true }}
                        className="text-sm font-medium text-[#174540] hover:text-[#0d2926]"
                    >
                        Retour à la liste
                    </Link>
                </div>
                
                <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                Vous modifiez la commande #{id}. Les champs marqués d'un astérisque (*) sont obligatoires.
                                {promoCodeState && promoCodeState.isValid && (
                                    <span className="block mt-1 text-green-700 font-medium">
                                        ✓ Code promo "{formData.client.promoCode}" appliqué (-{promoCodeState.discount} TND)
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto">
                <form onSubmit={onSubmitForm} className="space-y-8">
                    <div className="bg-white overflow-hidden sm:rounded-lg border border-gray-200">
                        <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center">
                                <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
                                    <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">Détails de la Commande</h3>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        Modifiez les informations de la commande ci-dessous
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-6 sm:px-8 sm:py-7">
                            <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Référence <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            name="reference"
                                            id="reference"
                                            value={formData.reference}
                                            onChange={handleInputChange}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-100 ${
                                                errors.reference ? 'border-red-300 text-red-900 placeholder-red-300' : ''
                                            }`}
                                        />
                                        {errors.reference && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {errors.reference && <p className="mt-2 text-sm text-red-600">{errors.reference}</p>}
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="dateBonCommande" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        Date de commande <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="datetime-local"
                                            name="dateBonCommande"
                                            id="dateBonCommande"
                                            value={formData.dateBonCommande}
                                            onChange={handleInputChange}
                                            className={`pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-[#174540] transition-all duration-300 ease-in-out text-sm rounded-t-md bg-gray-50 ${errors.dateBonCommande ? 'border-red-300 text-red-900 placeholder-red-300' : ''
                                                }`}
                                        />
                                        {errors.dateBonCommande && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {errors.dateBonCommande && <p className="mt-2 text-sm text-red-600">{errors.dateBonCommande}</p>}
                                </div>

                                <div className="sm:col-span-3">
                                    <OrderStatusSelector
                                        status={formData.etat}
                                        onChange={handleStatusChange}
                                        options={statusOptions}
                                        error={errors.etat}
                                    />
                                </div>

                                <div className="sm:col-span-3">
                                    <PlatformSelector
                                        selectedPlatform={formData.platformId}
                                        onChange={handleInputChange}
                                        error={errors.platformId}
                                    />
                                </div>

                                <div className="sm:col-span-3">
                                    <DepotSelector
                                        value={formData.depotId}
                                        onChange={handleInputChange}
                                        error={errors.depotId}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <ClientDetailsForm
                        formData={formData}
                        onChange={handleInputChange}
                        errors={errors}
                        promoCodeState={promoCodeState}
                        onPromoCodeBlur={handlePromoCodeBlur}
                        isEditMode={isEditMode}
                    />

                    <OrderItemsTable
                        items={orderItems}
                        onAddItem={handleAddItem}
                        onUpdateItem={handleUpdateItem}
                        onRemoveItem={handleRemoveItem}
                        totals={totals}
                        onUpdateTotals={updateTotals}
                    />

                    <OrderSummary 
                        totals={totals} 
                        items={orderItems}
                    />

                    <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200">
                        <Link
                            to="/admin/orders"
                            state={{ fromOrderEdit: true }}
                            className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                        >
                            Annuler
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            onClick={(e) => {
                                onSubmitForm(e);
                            }}
                            className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                                }`}
                        >
                            {isSubmitting ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Mise à jour...
                                </div>
                            ) : (
                                'Mettre à jour la commande'
                            )}
                        </button>
                    </div>
                </form>
            </div>
            
            {Object.keys(errors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                    <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
                    <ul className="mt-2 text-sm text-red-700">
                        {Object.entries(errors).map(([key, value]) => (
                            <li key={key}>{key}: {value}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default EditOrder;